import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { LoginValidationComponent } from '../../../book-create/login-validation.component/login-validation.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { AlertService, AlertType } from '../../../shared/services/alert.service';

import { AdminWithdrawalService } from '../../../shared/services/apis/platform/admin-withdrawal.service';
import { AdminWithdrawalListResponse } from '../../../shared/services/responses/platform/admin-withdrawal-list.response';
import { AdminWalletListStruct } from '../../../shared/services/responses/platform/admin-withdrawal-list.response';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-admin-withdrawal',
  standalone: true,
  templateUrl: './admin-withdrawal.component.html',
  styleUrl: './admin-withdrawal.component.css',
  imports: [
    CommonModule,
    MenuComponent,
    FooterComponent,
    LoginValidationComponent,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatButtonModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminWithdrawalComponent implements OnInit {

  isLoading = false;
  hasAccess = false;

  list: AdminWalletListStruct[] = [];
  total = 0;

  constructor(
    private svc: AdminWithdrawalService,
    private alert: AlertService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onLoginValidated(isLogged: boolean) {
    if (!isLogged) return;
    this.fetch();
  }

  fetch(): void {
    this.isLoading = true;
    this.cdr.markForCheck();

    this.svc.postList().subscribe({
      next: (res: AdminWithdrawalListResponse) => {
        if (res.isError) {
          this.alert.show('Erro', res.errorDescription || 'Falha ao carregar solicitações.', AlertType.error);
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }

        this.list = (res.listAdminWalletListStruct || []).map(x => ({
          ...x,
          // nada adicional; mantemos a estrutura original
        }));

        this.total = this.list.length;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.alert.show('Erro', 'Erro inesperado ao carregar a lista.', AlertType.error);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  // ---- Ações ----
  async onSelectReceipt(item: AdminWalletListStruct, ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const confirmed = await this.confirmIrreversible(
      'Confirmar pagamento',
      `Você vai confirmar o pagamento da retirada #${item.idWalletWithdrawal} do autor ${item.userName ?? item.idUser}.
O comprovante "${file.name}" (${(file.size/1024/1024).toFixed(2)} MB) será anexado. Esta ação NÃO pode ser desfeita. Deseja continuar?`,
      'Sim, confirmar pagamento'
    );

    if (!confirmed) { input.value = ''; return; }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.svc.confirmPayment(item.idWalletWithdrawal, file).subscribe({
      next: (res) => {
        if (res.isError) {
          this.alert.show('Erro', res.errorDescription || 'Falha ao confirmar pagamento.', AlertType.error);
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }
        this.alert.show('Sucesso', 'Pagamento confirmado e comprovante anexado.', AlertType.success);
        input.value = '';
        this.fetch();
      },
      error: () => {
        this.alert.show('Erro', 'Erro inesperado ao confirmar o pagamento.', AlertType.error);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  openUrl(url?: string | null) {
    if (!url) return;
    window.open(url, '_blank', 'noopener');
  }

  // ---- Helpers ----
  /**
   * Status calculado:
   * - Se NÃO existe comprovante E NÃO existe processedAt => Requisitado (0)
   * - Caso contrário => Pago (2)
   * Obs: Você também listou "Processando (1)" e "Rejeitado (3)". Como a regra operacional aqui é:
   *   "Ao confirmar, envia comprovante e considera PAGO", então o fluxo desta tela só usa 0 ou 2.
   */
  computeStatus(item: AdminWalletListStruct): number {
    const hasReceipt = !!item.receipt;
    const hasProcessedAt = !!item.processedAt;
    if (!hasReceipt && !hasProcessedAt) return 0; // Requisitado
    return 2; // Pago
  }

  statusText(item: AdminWalletListStruct): string {
    const s = this.computeStatus(item);
    if (s === 0) return 'Requisitado';
    if (s === 1) return 'Processando';
    if (s === 2) return 'Pago';
    if (s === 3) return 'Rejeitado';
    return '—';
    // Nota: por regra desta tela, só entra 0 ou 2.
  }

  statusClass(item: AdminWalletListStruct): string {
    const s = this.computeStatus(item);
    if (s === 0) return 'chip chip-info';     // Requisitado
    if (s === 1) return 'chip chip-pending';  // (não usado aqui)
    if (s === 2) return 'chip chip-success';  // Pago
    if (s === 3) return 'chip chip-warn';     // Rejeitado
    return 'chip';
  }

private confirmIrreversible(title: string, description: string, buttonConfirm = 'Confirmar'): Promise<boolean> {
  const ref = this.dialog.open(ConfirmModalComponent, {
    data: { title, description, buttonConfirm, buttonClose: 'Cancelar' }
  });
  return new Promise(resolve => ref.afterClosed().subscribe(r => resolve(!!r?.confirm)));
}

  trackById = (_: number, it: AdminWalletListStruct) => it?.idWalletWithdrawal ?? _;
}
